import { Article, DefaultArticleItemState, ECounterType, Note } from "@tm/models"
import { useCallback, useMemo, useState } from "react"
import { TmaHelper } from "@tm/utils"
import { useVehicle } from "../useVehicle"
import { getArticleNoteIdentifier } from "../../../_shared/article-item/note/helper/mapper"
import { useListParamsContext, useNotesContext } from "../../ContextProvider"

export function useArticleNotes(article: Article): DefaultArticleItemState["notes"] {
    const vehicle = useVehicle()
    const { notes: allNotes, deleteNote, saveNote } = useNotesContext()
    const { listType } = useListParamsContext()

    const [isLoading, setIsLoading] = useState(false)
    const [visible, setVisible] = useState(false)

    const notes = useMemo(() => {
        const noteIds = getArticleNoteIdentifier(article, vehicle)
        return allNotes.filter((note) => noteIds.includes(note.noteId))
    }, [allNotes, article, vehicle, listType])

    const handleSave = useCallback(
        async (note: Note) => {
            setIsLoading(true)
            await saveNote(note)
            setIsLoading(false)
        },
        [saveNote]
    )

    const handleDelete = useCallback(
        async (noteId: string) => {
            setIsLoading(true)
            await deleteNote(noteId)
            setIsLoading(false)
        },
        [deleteNote]
    )

    const toggle = useCallback(() => {
        TmaHelper.GeneralCountEvent.Call(ECounterType.ArticleListArticleNotes, !visible)
        setVisible((prev) => !prev)
    }, [visible])

    const hide = useCallback(() => {
        setVisible(false)
    }, [])

    return useMemo(
        () => ({
            notes,
            handleSave,
            handleDelete,
            isLoading,
            visible,
            toggle,
            hide,
        }),
        [notes, handleSave, handleDelete, isLoading, visible, toggle, hide]
    )
}
